import { useState, useEffect, useCallback } from 'react';
import { Target, Wallet, Pencil, Trash2, X, Briefcase } from 'lucide-react';
import ModalShell from '../../ui/ModalShell';
import { SECTION_COLOR, datum } from '../lide';
import { Sekce, SekceAkce, Pole, Vstup, Vyber, Tlacitko, PotvrzeniModal, muted, linka } from './ui';

/**
 * Karta zaměstnance → Kariéra a mzda.
 *
 * Kariérní cíle, fixní plat a souběžná DPP.
 *
 * **Plat se řídí kariérou:** fixní plat kryje svou kariéru (směny v ní jsou
 * v platu), v ostatních kariérách se platí hodinovka podle dosažené úrovně.
 * Má-li člověk k hlavnímu úvazku souběžnou DPP, jdou směny mimo kariéru fixu
 * na DPP (výplata po směně, v uzávěrce jako DPP) — `PokrytiFixem` na serveru.
 *
 * Přidání a úprava jsou rychlé akce se začátkem
 * a koncem → `ModalShell` (norma §1f); dřív se formulář rozbaloval přímo
 * v tabulce a úprava řádku měnila buňky na pole. Úrovně se nekreslí
 * v pěti barvách (šedá, žlutá, modrá, fialová, oranžová) — úroveň je text.
 */

const ODVODY = 0.338;
const kc = (n) => `${Math.round(Number(n) || 0).toLocaleString('cs-CZ')} Kč`;
const naklad = (fix, bonus) => Math.round((Number(fix) || 0) * (1 + ODVODY) + (Number(bonus) || 0));

export default function KarieraMzda({ z, isDark, isMobile, muzuEditovat, onZmena }) {
    const [karieryCis, setKarieryCis] = useState([]);
    const [platy, setPlaty] = useState([]);
    const [picker, setPicker] = useState(false);
    const [platForm, setPlatForm] = useState(null);        // { id?, castka_kc, variabilni_bonus_kc, platnost_od, platnost_do?, kariera_id }
    const [smazat, setSmazat] = useState(null);            // { typ: 'plat'|'kariera', polozka }
    const [dppForm, setDppForm] = useState(null);          // { od, do, prace }
    const mut = muted(isDark);

    const nactiPlaty = useCallback(() => {
        fetch(`/api/zamestnanci/${z.id}/fixni-plat`).then(r => r.json()).then(d => setPlaty(Array.isArray(d) ? d : [])).catch(() => {});
    }, [z.id]);

    useEffect(() => { nactiPlaty(); }, [nactiPlaty]);
    useEffect(() => {
        fetch('/api/ciselniky/kariery').then(r => r.json()).then(d => setKarieryCis(Array.isArray(d) ? d : [])).catch(() => {});
    }, []);

    const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content;
    const karieryZam = z.kariery || [];
    const karieryOpce = (z.urovne || []).map(u => ({ id: u.kariera_id || u.kariera?.id, nazev: u.kariera?.nazev })).filter(k => k.id);
    const nazevKariery = (fp) => fp.kariera_id == null ? 'kryje všechny kariéry'
        : `kryje ${fp.kariera?.nazev || karieryOpce.find(k => k.id === fp.kariera_id)?.nazev || `kariéru #${fp.kariera_id}`}`;

    // Dnes platný fixní plat a souběžná DPP — podle nich se u každé kariéry
    // ukáže, jak se platí.
    const dnes = new Date().toISOString().split('T')[0];
    const fixyDnes = platy.filter(fp => fp.platnost_od <= dnes && (!fp.platnost_do || fp.platnost_do >= dnes));
    const dppDnes = !!z.soubezna_dpp_od && z.soubezna_dpp_od.slice(0, 10) <= dnes
        && (!z.soubezna_dpp_do || z.soubezna_dpp_do.slice(0, 10) >= dnes);
    const hlavniDpp = ['DPP', 'DPC'].includes(z.typ_uvazku?.kod);
    const jakSePlati = (karieraId, u) => {
        if (fixyDnes.some(fp => fp.kariera_id == null || fp.kariera_id === karieraId)) return 'fixní plat';
        if (!u?.sazba_kc_h) return null;
        return `${u.sazba_kc_h} Kč/h${dppDnes && !hlavniDpp ? ' · DPP' : ''}`;
    };

    const ulozDpp = async () => {
        await fetch(`/api/zamestnanci/${z.id}`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-TOKEN': csrf() },
            body: JSON.stringify({ soubezna_dpp_od: dppForm.od || null, soubezna_dpp_do: dppForm.do || null, soubezna_dpp_prace: dppForm.prace || null }),
        });
        setDppForm(null);
        onZmena?.();
    };

    const odebratKarieru = async (k) => {
        const ids = karieryZam.map(x => x.id).filter(id => id !== k.id);
        await fetch(`/api/zamestnanci/${z.id}`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-TOKEN': csrf() },
            body: JSON.stringify({ kariera_ids: ids }),
        });
        onZmena?.();
    };

    const nastavCil = async (kariera, uroven) => {
        await fetch(`/api/zamestnanci/${z.id}/kariera-cil`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-TOKEN': csrf() },
            body: JSON.stringify({ kariera_id: kariera.id, cil_uroven_id: uroven.id }),
        });
        setPicker(false);
        onZmena?.();
    };

    const ulozPlat = async () => {
        const f = platForm;
        if (!f.castka_kc || !f.platnost_od) return;
        if (f.id) {
            await fetch(`/api/konfigurace/fixni-plat-zaznam/${f.id}`, {
                method: 'PUT', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ castka_kc: parseFloat(f.castka_kc), variabilni_bonus_kc: parseFloat(f.variabilni_bonus_kc) || 0, platnost_od: f.platnost_od, platnost_do: f.platnost_do || null, kariera_id: f.kariera_id ? parseInt(f.kariera_id, 10) : null }),
            });
        } else {
            await fetch(`/api/konfigurace/fixni-plat/${z.id}`, {
                method: 'PUT', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ kariera_id: f.kariera_id ? parseInt(f.kariera_id, 10) : null, castka_kc: parseFloat(f.castka_kc), variabilni_bonus_kc: parseFloat(f.variabilni_bonus_kc) || 0, platnost_od: f.platnost_od }),
            });
        }
        setPlatForm(null); nactiPlaty(); onZmena?.();
    };

    return (
        <>
            <Sekce nazev="Kariérní cíle" isDark={isDark} prvni
                akce={muzuEditovat ? <SekceAkce isMobile={isMobile} onClick={() => setPicker(true)}>+ Přidat cíl</SekceAkce> : null}>
                {karieryZam.length === 0 ? (
                    <p className="text-[13px]" style={{ color: mut, padding: '6px 0' }}>Žádné kariérní cíle.</p>
                ) : karieryZam.map(k => {
                    const u = z.urovne?.find(x => (x.kariera_id || x.kariera?.id) === k.id);
                    const urovne = u?.kariera?.urovne || karieryCis.find(c => c.id === k.id)?.urovne || [];
                    const cil = u?.cil_uroven_id ? urovne.find(x => x.id === u.cil_uroven_id) : null;
                    const aktualni = u?.uroven?.nazev || 'Novice';
                    const sazba = jakSePlati(k.id, u);
                    return (
                        <div key={k.id} className="flex items-center" style={{ gap: 10, minHeight: isMobile ? 52 : 40, borderBottom: `1px solid ${linka(isDark)}` }}>
                            <div className="min-w-0 flex-1">
                                <span className="text-[13px] font-semibold" style={{ color: 'var(--text-primary)' }}>{k.nazev}</span>
                                <span className="text-[12.5px]" style={{ color: mut, marginLeft: 8 }}>
                                    {aktualni}{cil && cil.nazev !== aktualni ? ` → cíl ${cil.nazev}` : ''}
                                    {u && !u.cert_ok && <span style={{ color: '#f87171', fontWeight: 600 }}> · chybí certifikát</span>}
                                </span>
                            </div>
                            {sazba && <span className="text-[12.5px] tabular-nums" style={{ color: 'var(--text-secondary)' }}>{sazba}</span>}
                            {muzuEditovat && !isMobile && (
                                <button type="button" title="Odebrat kariéru" aria-label={`Odebrat kariéru ${k.nazev}`}
                                    onClick={() => setSmazat({ typ: 'kariera', polozka: k })}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: mut, padding: 6, display: 'flex' }}>
                                    <X size={15} />
                                </button>
                            )}
                        </div>
                    );
                })}
            </Sekce>

            <Sekce nazev="Fixní plat" isDark={isDark}
                akce={muzuEditovat ? (
                    <SekceAkce isMobile={isMobile}
                        onClick={() => setPlatForm({ castka_kc: '', variabilni_bonus_kc: '', platnost_od: new Date().toISOString().split('T')[0], kariera_id: '' })}>
                        + Nový plat
                    </SekceAkce>
                ) : null}>
                <p className="text-[12px]" style={{ color: mut, margin: '2px 0 6px' }}>
                    Fix kryje svou kariéru — směny v ní jsou v platu. V ostatních kariérách se platí hodinovka podle dosažené úrovně.
                </p>
                {platy.length === 0 ? (
                    <p className="text-[13px]" style={{ color: mut, padding: '6px 0' }}>Žádný záznam fixního platu.</p>
                ) : platy.map(fp => {
                    const aktivni = fp.platnost_do == null;
                    return (
                        <div key={fp.id} className="flex items-center" style={{ gap: 12, minHeight: isMobile ? 56 : 44, borderBottom: `1px solid ${linka(isDark)}` }}>
                            <div className="min-w-0 flex-1">
                                <p className="text-[13px] font-semibold tabular-nums" style={{ color: 'var(--text-primary)' }}>
                                    {kc(fp.castka_kc)}
                                    {Number(fp.variabilni_bonus_kc) > 0 && <span style={{ color: mut, fontWeight: 500 }}> + bonus {kc(fp.variabilni_bonus_kc)}</span>}
                                </p>
                                <p className="text-[12px]" style={{ color: mut }}>
                                    {datum(fp.platnost_od)} – {aktivni ? <b style={{ color: 'var(--text-secondary)' }}>dosud</b> : datum(fp.platnost_do)} · {nazevKariery(fp)}
                                </p>
                            </div>
                            <div className="text-right">
                                <p className="text-[13px] font-semibold tabular-nums" style={{ color: 'var(--text-primary)' }}>{kc(naklad(fp.castka_kc, fp.variabilni_bonus_kc))}</p>
                                <p className="text-[11px]" style={{ color: mut }}>náklad / měs</p>
                            </div>
                            {muzuEditovat && !isMobile && (
                                <div className="flex" style={{ gap: 2 }}>
                                    <button type="button" title="Upravit" aria-label="Upravit záznam platu"
                                        onClick={() => setPlatForm({ id: fp.id, castka_kc: fp.castka_kc, variabilni_bonus_kc: fp.variabilni_bonus_kc || '', platnost_od: fp.platnost_od, platnost_do: fp.platnost_do, kariera_id: fp.kariera_id ?? '' })}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: mut, padding: 6, display: 'flex' }}>
                                        <Pencil size={14} />
                                    </button>
                                    <button type="button" title="Smazat" aria-label="Smazat záznam platu"
                                        onClick={() => setSmazat({ typ: 'plat', polozka: fp })}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: mut, padding: 6, display: 'flex' }}>
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })}
            </Sekce>

            <Sekce nazev="Souběžná DPP" isDark={isDark}
                akce={muzuEditovat && !hlavniDpp ? (
                    <SekceAkce isMobile={isMobile}
                        onClick={() => setDppForm({ od: z.soubezna_dpp_od?.slice(0, 10) || dnes, do: z.soubezna_dpp_do?.slice(0, 10) || '', prace: z.soubezna_dpp_prace || '' })}>
                        {z.soubezna_dpp_od ? 'Upravit' : '+ Přidat'}
                    </SekceAkce>
                ) : null}>
                <p className="text-[12px]" style={{ color: mut, margin: '2px 0 6px' }}>
                    Druhý úvazek vedle hlavního. Směny mimo kariéru fixu jdou na DPP — hodinovka podle úrovně, výplata po směně, v uzávěrce jako DPP.
                </p>
                {hlavniDpp ? (
                    <p className="text-[13px]" style={{ color: mut, padding: '6px 0' }}>Hlavní úvazek je už DPP/DPC.</p>
                ) : !z.soubezna_dpp_od ? (
                    <p className="text-[13px]" style={{ color: mut, padding: '6px 0' }}>Nemá.</p>
                ) : (
                    <div className="flex items-center" style={{ gap: 12, minHeight: isMobile ? 56 : 44, borderBottom: `1px solid ${linka(isDark)}` }}>
                        <div className="min-w-0 flex-1">
                            <p className="text-[13px] font-semibold" style={{ color: 'var(--text-primary)' }}>{z.soubezna_dpp_prace || 'DPP'}</p>
                            <p className="text-[12px]" style={{ color: mut }}>
                                {datum(z.soubezna_dpp_od)} – {z.soubezna_dpp_do ? datum(z.soubezna_dpp_do) : <b style={{ color: 'var(--text-secondary)' }}>dosud</b>}
                                {!dppDnes && ' · dnes neplatí'}
                            </p>
                        </div>
                    </div>
                )}
            </Sekce>

            {dppForm && (
                <ModalShell onClose={() => setDppForm(null)} isDark={isDark} barva={SECTION_COLOR} ikona={Briefcase}
                    nadpis="Souběžná DPP"
                    paticka={(
                        <div className="flex gap-2 justify-end">
                            {z.soubezna_dpp_od && (
                                <Tlacitko isDark={isDark} vyska={40} onClick={async () => {
                                    await fetch(`/api/zamestnanci/${z.id}`, {
                                        method: 'PUT', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-TOKEN': csrf() },
                                        body: JSON.stringify({ soubezna_dpp_od: null, soubezna_dpp_do: null, soubezna_dpp_prace: null }),
                                    });
                                    setDppForm(null); onZmena?.();
                                }}>Zrušit DPP</Tlacitko>
                            )}
                            <span style={{ flex: 1 }} />
                            <Tlacitko isDark={isDark} vyska={40} onClick={() => setDppForm(null)}>Zavřít</Tlacitko>
                            <Tlacitko isDark={isDark} vyska={40} primarni onClick={ulozDpp} disabled={!dppForm.od}>Uložit</Tlacitko>
                        </div>
                    )}>
                    <div style={{ padding: 16, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                        <Pole label="Platnost od" isDark={isDark}>
                            <Vstup isDark={isDark} type="date" value={dppForm.od} onChange={e => setDppForm(f => ({ ...f, od: e.target.value }))} />
                        </Pole>
                        <Pole label="Platnost do (volitelné)" isDark={isDark}>
                            <Vstup isDark={isDark} type="date" value={dppForm.do} onChange={e => setDppForm(f => ({ ...f, do: e.target.value }))} />
                        </Pole>
                        <div style={{ gridColumn: '1 / -1' }}>
                            <Pole label="Druh práce" isDark={isDark}>
                                <Vstup isDark={isDark} value={dppForm.prace} placeholder="např. pomocná obsluha"
                                    onChange={e => setDppForm(f => ({ ...f, prace: e.target.value }))} />
                            </Pole>
                        </div>
                    </div>
                </ModalShell>
            )}

            {platForm && (
                <ModalShell onClose={() => setPlatForm(null)} isDark={isDark} barva={SECTION_COLOR} ikona={Wallet}
                    nadpis={platForm.id ? 'Upravit fixní plat' : 'Nový fixní plat'}
                    paticka={(
                        <div className="flex gap-2 justify-end">
                            <Tlacitko isDark={isDark} vyska={40} onClick={() => setPlatForm(null)}>Zrušit</Tlacitko>
                            <Tlacitko isDark={isDark} vyska={40} primarni onClick={ulozPlat} disabled={!platForm.castka_kc || !platForm.platnost_od}>Uložit</Tlacitko>
                        </div>
                    )}>
                    <div style={{ padding: 16, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                        <Pole label="Fixní plat Kč/měs" isDark={isDark}>
                            <Vstup isDark={isDark} type="number" inputMode="numeric" value={platForm.castka_kc} placeholder="např. 35000"
                                onChange={e => setPlatForm(f => ({ ...f, castka_kc: e.target.value }))} />
                        </Pole>
                        <Pole label="Variabilní bonus Kč/měs" isDark={isDark}>
                            <Vstup isDark={isDark} type="number" inputMode="numeric" value={platForm.variabilni_bonus_kc} placeholder="0"
                                onChange={e => setPlatForm(f => ({ ...f, variabilni_bonus_kc: e.target.value }))} />
                        </Pole>
                        <Pole label="Platnost od" isDark={isDark}>
                            <Vstup isDark={isDark} type="date" value={platForm.platnost_od} onChange={e => setPlatForm(f => ({ ...f, platnost_od: e.target.value }))} />
                        </Pole>
                        {platForm.id && (
                            <Pole label="Platnost do" isDark={isDark}>
                                <Vstup isDark={isDark} type="date" value={platForm.platnost_do || ''} onChange={e => setPlatForm(f => ({ ...f, platnost_do: e.target.value || null }))} />
                            </Pole>
                        )}
                        <Pole label="Kryje kariéru" isDark={isDark}>
                            <Vyber isDark={isDark} value={platForm.kariera_id} placeholder="Všechny kariéry"
                                onChange={v => setPlatForm(f => ({ ...f, kariera_id: v }))}
                                options={karieryOpce.map(k => ({ value: k.id, label: k.nazev }))} />
                        </Pole>
                        {parseFloat(platForm.castka_kc) > 0 && (
                            <div className="text-[12.5px]" style={{ gridColumn: '1 / -1', color: mut }}>
                                Odvody 33,8 %: <b style={{ color: 'var(--text-secondary)' }}>{kc(parseFloat(platForm.castka_kc) * ODVODY)}</b>
                                {' · '}náklad celkem: <b style={{ color: 'var(--text-primary)' }}>{kc(naklad(platForm.castka_kc, platForm.variabilni_bonus_kc))}</b> / měs
                            </div>
                        )}
                    </div>
                </ModalShell>
            )}

            {picker && (
                <ModalShell onClose={() => setPicker(false)} isDark={isDark} barva={SECTION_COLOR} ikona={Target} nadpis="Přidat kariérní cíl" sirka={480}>
                    <div style={{ padding: '8px 0 12px' }}>
                        {karieryCis.length === 0 && <p className="text-xs text-center" style={{ color: mut, padding: 24 }}>Žádné kariéry v systému</p>}
                        {karieryCis.map(kariera => {
                            const prirazena = karieryZam.some(k => k.id === kariera.id);
                            const prirazenaUroven = z.urovne?.find(u => u.kariera_id === kariera.id);
                            const urovne = [...(kariera.urovne || [])].sort((a, b) => a.poradi - b.poradi);
                            return (
                                <div key={kariera.id} style={{ padding: '6px 16px' }}>
                                    <p className="text-[11px] font-bold uppercase" style={{ color: mut, letterSpacing: '0.06em', padding: '6px 0' }}>
                                        {kariera.nazev}{prirazena ? ' · přiřazeno' : ''}
                                    </p>
                                    {urovne.map(ur => {
                                        const aktualniCil = prirazena && prirazenaUroven?.cil_uroven_id === ur.id;
                                        return (
                                            <button key={ur.id} type="button" disabled={aktualniCil} onClick={() => nastavCil(kariera, ur)}
                                                className="w-full flex items-center text-left rounded-lg disabled:opacity-50"
                                                style={{ gap: 12, minHeight: isMobile ? 48 : 38, padding: '0 10px', background: 'none', border: 'none', cursor: aktualniCil ? 'default' : 'pointer', color: 'var(--text-primary)', fontSize: 13 }}
                                                onMouseEnter={e => { if (!aktualniCil) e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'; }}
                                                onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>
                                                <span className="font-semibold" style={{ minWidth: 72 }}>{ur.nazev}</span>
                                                <span className="text-[12.5px]" style={{ color: mut }}>{ur.nazev === 'Leader' ? 'fixní plat' : ur.sazba_kc_h ? `${ur.sazba_kc_h} Kč/h` : ''}</span>
                                                {aktualniCil && <span className="text-[11px]" style={{ marginLeft: 'auto', color: SECTION_COLOR }}>aktuální cíl</span>}
                                            </button>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>
                </ModalShell>
            )}

            {smazat && (
                <PotvrzeniModal isDark={isDark} onZavri={() => setSmazat(null)}
                    nadpis={smazat.typ === 'plat' ? 'Smazat záznam fixního platu?' : `Odebrat kariéru ${smazat.polozka.nazev}?`}
                    text={smazat.typ === 'plat'
                        ? `Záznam ${kc(smazat.polozka.castka_kc)} od ${datum(smazat.polozka.platnost_od)} zmizí i z nákladů ve výsledovce.`
                        : 'Zaměstnanec přestane mít tuto kariéru — i její úroveň a sazbu.'}
                    potvrdit={smazat.typ === 'plat' ? 'Smazat' : 'Odebrat'}
                    onPotvrdit={async () => {
                        if (smazat.typ === 'plat') {
                            await fetch(`/api/konfigurace/fixni-plat-zaznam/${smazat.polozka.id}`, { method: 'DELETE', headers: { Accept: 'application/json' } });
                            nactiPlaty(); onZmena?.();
                        } else {
                            await odebratKarieru(smazat.polozka);
                        }
                        setSmazat(null);
                    }} />
            )}
        </>
    );
}

